import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { TrendingUp, ArrowLeft, Loader2, Globe, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

export default function Login() {
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const { login, verifyLogin2FA } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [pendingToken, setPendingToken] = useState(null);
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRequired = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.identifier || !formData.password) {
      toast.error(t('Please fill all fields', 'Tafadhali jaza sehemu zote'));
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      toast.error(t('Please wait for the security check to finish',
                    'Tafadhali subiri uthibitisho wa usalama ukamilike'));
      turnstileRef.current?.reset();
      return;
    }

    setLoading(true);
    try {
      const result = await login(formData.identifier, formData.password, turnstileToken);
      if (result?.requires_2fa) {
        setPendingToken(result.pending_token);
        toast.info(t('A verification code has been sent. Enter it to continue.',
                     'Msimbo wa uthibitisho umetumwa. Ingiza ili kuendelea.'));
        return;
      }
      toast.success(t('Welcome back!', 'Karibu tena!'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Login failed', 'Kuingia kumeshindikana'));
    } finally {
      setLoading(false);
      // Tokens are single-use: reset the widget so a retry gets a fresh one.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error(t('Enter the 6-digit code', 'Ingiza msimbo wa nambari 6'));
      return;
    }
    setLoading(true);
    try {
      await verifyLogin2FA(pendingToken, otp);
      toast.success(t('Welcome back!', 'Karibu tena!'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Verification failed', 'Uthibitisho umeshindikana'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-primary/90" />
        <img 
          src="https://images.unsplash.com/photo-1758273237946-26d40a31f02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIweW91bmclMjBwcm9mZXNzaW9uYWwlMjB1c2luZyUyMHBob25lJTIwaGFwcHklMjBjaXR5fGVufDB8fHx8MTc3MDk4NjQ1OHww&ixlib=rb-4.1.0&q=85"
          alt="Professional using technology"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="font-bold text-2xl">Wekeza-Smart</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            {t('Welcome Back', 'Karibu Tena')}
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            {t(
              'Continue your investment education journey. Your AI coach is ready to help.',
              'Endelea safari yako ya elimu ya uwekezaji. Mshauri wako wa AI yuko tayari kusaidia.'
            )}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <header className="p-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back', 'Rudi')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={toggleLanguage}
            className="flex items-center gap-2"
            data-testid="login-language-toggle"
          >
            <Globe className="w-4 h-4" />
            {language === 'en' ? 'Kiswahili' : 'English'}
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="text-center lg:text-left">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl">Wekeza-Smart</span>
              </div>
              <CardTitle className="text-2xl">
                {t('Sign In', 'Ingia')}
              </CardTitle>
              <CardDescription>
                {t('Enter your email or phone number to continue', 'Ingiza barua pepe au nambari ya simu kuendelea')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!pendingToken ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">
                    {t('Email or Phone', 'Barua Pepe au Simu')}
                  </Label>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder={t('e.g. john@email.com or 0712345678', 'mfano john@email.com au 0712345678')}
                    value={formData.identifier}
                    onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                    className="h-12"
                    data-testid="login-identifier-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">
                      {t('Password', 'Nenosiri')}
                    </Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t('Forgot password?', 'Umesahau nenosiri?')}
                    </Link>
                  </div>
                  <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="h-12 pr-12"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                    aria-label={showPassword ? t('Hide password', 'Ficha nenosiri') : t('Show password', 'Onyesha nenosiri')}
                    data-testid="login-toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-full text-base btn-press"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Signing in...', 'Inaingia...')}
                    </>
                  ) : (
                    t('Sign In', 'Ingia')
                  )}
                </Button>
                <TurnstileWidget ref={turnstileRef} action="login" onToken={setTurnstileToken} />
              </form>
              ) : (
                <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="login-2fa-form">
                  <div className="space-y-2">
                    <Label htmlFor="otp">
                      {t('6-Digit Verification Code', 'Msimbo wa Uthibitisho wa Nambari 6')}
                    </Label>
                    <Input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="h-12 text-center text-2xl tracking-[0.5em] font-mono"
                      data-testid="login-otp-input"
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('Code expires in 10 minutes.', 'Msimbo unaisha baada ya dakika 10.')}
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full text-base btn-press"
                    disabled={loading}
                    data-testid="login-2fa-verify-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('Verifying...', 'Inathibitisha...')}
                      </>
                    ) : (
                      t('Verify & Sign In', 'Thibitisha na Ingia')
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => { setPendingToken(null); setOtp(''); }}
                    data-testid="login-2fa-cancel-btn"
                  >
                    {t('Use a different account', 'Tumia akaunti tofauti')}
                  </Button>
                </form>
              )}
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("Don't have an account?", "Huna akaunti?")}{' '}
                  <Link 
                    to="/register" 
                    className="text-primary font-medium hover:underline"
                    data-testid="login-register-link"
                  >
                    {t('Sign up', 'Jisajili')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
